import { useQuery } from '@tanstack/react-query';
import { workspaceApi } from './api';

/**
 * The scheduling time zone (QUESTIONS Q12, D-146): posts, stories and
 * campaigns are planned and shown in the workspace's zone, never the
 * browser's, so everyone in the workspace reads the same "10:00 ·
 * Europe/Paris". The composer's date and time pills hold a wall time in that
 * zone; these helpers turn it into an instant and back.
 */

/** The zone a workspace gets when none was ever set (the server's default). */
export const FALLBACK_ZONE = 'UTC';

export function isValidTimeZone(zone: string | null | undefined): zone is string {
  if (!zone) return false;
  try {
    new Intl.DateTimeFormat('en', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** A usable IANA zone: the workspace's, or UTC when it is missing or unreadable. */
export const schedulingZoneOf = (zone: string | null | undefined): string => (isValidTimeZone(zone) ? zone : FALLBACK_ZONE);

/** The workspace's scheduling zone; null until the workspace has loaded. */
export function useSchedulingZone(): string | null {
  const workspace = useQuery({ queryKey: ['workspace', 'current'], queryFn: workspaceApi.current });
  return workspace.data ? schedulingZoneOf(workspace.data.timezone) : null;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** The wall-clock fields of an instant in a zone. */
function wallClock(date: Date, zone: string) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value);
  return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour'), minute: get('minute') };
}

/** How far the zone's clock is ahead of UTC at that instant, in ms (minute precision). */
function offsetAt(instant: number, zone: string): number {
  const w = wallClock(new Date(instant), zone);
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute);
  return asUtc - (instant - (instant % 60_000));
}

/** `2027-03-15T10:00`: an instant as a `datetime-local` value on the zone's clock. */
export function toZonedInput(date: Date, zone: string): string {
  const w = wallClock(date, zone);
  return `${w.year}-${pad(w.month)}-${pad(w.day)}T${pad(w.hour)}:${pad(w.minute)}`;
}

/**
 * The instant a `datetime-local` value means on the zone's clock, or null
 * when it is not a complete date and time. A time skipped by a clock change
 * (02:30 on 28 March 2027 in Paris) moves forward by the jump (03:30).
 */
export function fromZonedInput(local: string, zone: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local);
  if (!match) return null;
  const [year, month, day, hour, minute] = match.slice(1).map(Number) as [number, number, number, number, number];
  const asUtc = Date.UTC(year, month - 1, day, hour, minute);
  if (Number.isNaN(asUtc)) return null;
  // Guess with the offset at the wall time read as UTC, then once more with the offset at the guess.
  const first = asUtc - offsetAt(asUtc, zone);
  return new Date(asUtc - offsetAt(first, zone));
}
